import { useEffect, useState } from 'react';
import { X, Check } from 'lucide-react';
import { Button } from '@techsofit/ui';
import { submitDemoRequest } from '../config';

/** Global "Book a demo" modal. Opens when any control fires the `techsofit:book-demo` event. */
export default function DemoModal() {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [business, setBusiness] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const onOpen = () => {
      setOpen(true);
      setDone(false);
      setError(null);
    };
    window.addEventListener('techsofit:book-demo', onOpen);
    return () => window.removeEventListener('techsofit:book-demo', onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  async function submit() {
    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!email.trim() && !phone.trim()) {
      setError('Add an email or phone number so we can reach you.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await submitDemoRequest({
        name: name.trim(),
        businessName: business.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        message: message.trim() || undefined,
      });
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-3 sm:items-center" role="dialog" aria-modal="true" aria-label="Book a demo">
      <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="absolute inset-0 bg-ink/40 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg overflow-hidden rounded-card bg-card p-6 shadow-pop sm:p-7">
        <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="absolute right-4 top-4 text-muted hover:text-ink">
          <X className="size-5" aria-hidden />
        </button>

        {done ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <span className="grid size-12 place-items-center rounded-full bg-accent-soft text-ink">
              <Check className="size-6" aria-hidden />
            </span>
            <h2 className="font-display text-2xl text-ink">Thanks, {name.split(' ')[0] || 'there'}!</h2>
            <p className="max-w-sm text-sm text-muted">
              We've got your request and will be in touch shortly to set up your demo.
            </p>
            <button type="button" onClick={() => setOpen(false)} className="mt-2 text-sm font-medium text-ink-soft hover:text-ink">
              Done
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="font-display text-2xl text-ink">Book a demo</h2>
              <p className="mt-1 text-sm text-muted">
                Tell us a little about your business and we'll show you around.
              </p>
            </div>

            {error && (
              <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">
                {error}
              </p>
            )}

            <Field label="Your name">
              <input value={name} onChange={(e) => setName(e.target.value)} autoFocus className={inputCls} placeholder="Full name" />
            </Field>
            <Field label="Business name (optional)">
              <input value={business} onChange={(e) => setBusiness(e.target.value)} className={inputCls} placeholder="Gym, studio, club or academy" />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Email">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@business.com" />
              </Field>
              <Field label="Phone">
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} placeholder="+263 77 000 0000" />
              </Field>
            </div>
            <Field label="Anything you'd like us to know? (optional)">
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} className={inputCls} placeholder="What are you hoping to solve?" />
            </Field>

            <div className="mt-1 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} disabled={saving} className="rounded-pill px-4 py-2.5 text-sm font-medium text-ink-soft hover:text-ink">
                Cancel
              </button>
              <Button onClick={submit} loading={saving}>Request demo</Button>
            </div>
            <p className="text-center text-xs text-muted">We'll only use your details to arrange the demo.</p>
          </div>
        )}
      </div>
    </div>
  );
}

const inputCls =
  'mt-1 w-full rounded-control border border-line bg-elevated px-3.5 py-2.5 text-sm text-ink placeholder:text-muted focus:border-ink-soft focus:outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm font-medium text-ink-soft">
      {label}
      {children}
    </label>
  );
}
